/**
 * API client.
 *
 * `credentials: 'include'` on every call, because the session lives in an
 * httpOnly cookie the JavaScript here cannot read — which is the point. There is
 * no token to store, so there is nothing for a cross-site script to steal.
 */

const BASE_URL = import.meta.env['VITE_API_BASE_URL'] || '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the session is absent, expired or awaiting a second factor. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/**
 * True when a request was called off by its caller rather than failing.
 *
 * Worth distinguishing: an abort is something the reader did on purpose, so it
 * must never surface as "cannot reach QuickStock".
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Who to tell when the API says the session is gone.
 *
 * Sessions end on the server for reasons this tab never hears about: the person
 * was removed from a store or had their role changed, their password was changed
 * on another device, or the session expired. From then on every request is a
 * 401, and with nobody listening the app went on showing whatever it had already
 * loaded, with "Authentication required" under each section, until a reload.
 *
 * AuthContext listens, and decides what a 401 means — while signing in it is
 * only a wrong password or code, which the sign-in form reports itself.
 */
let sessionEndedListener: (() => void) | null = null;

export function onSessionEnded(listener: (() => void) | null): void {
  sessionEndedListener = listener;
}

/** Called with every API response, by each of the clients that fetch it. */
export function noticeSessionEnded(response: Response): void {
  if (response.status === 401) sessionEndedListener?.();
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch (caught) {
    // An abort arrives here too, and it is not a connection problem — pass it
    // through so the caller can tell the two apart.
    if (init.signal?.aborted) throw caught;

    // fetch only rejects on a network-level failure, which for this app almost
    // always means the device is offline rather than the server being broken.
    throw new ApiError(0, 'Cannot reach QuickStock. Check your connection.');
  }

  noticeSessionEnded(response);
  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const body = payload as { error?: string; requestId?: string } | null;
    throw new ApiError(
      response.status,
      body?.error ?? `Request failed (${response.status})`,
      body?.requestId,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  post: <T>(path: string, body?: unknown, options?: { signal?: AbortSignal }): Promise<T> =>
    request<T>(path, {
      method: 'POST',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(options?.signal ? { signal: options.signal } : {}),
    }),
  put: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'PUT',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  // DELETE with a body: the push endpoint identifies the subscription, and it
  // is too long to sit safely in a query string.
  delete: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'DELETE',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
};

export interface SessionUser {
  userId: string;
  email: string;
  fullName: string;
  totpEnabled: boolean;
}

export type StoreRole = 'owner' | 'manager' | 'viewer';

export interface GrantedStore {
  storeId: string;
  slug: string;
  displayName: string;
  timezone: string;
  currency: string;
  role: StoreRole;
}

export interface MeResponse {
  user: SessionUser;
  stores: GrantedStore[];
}

/** What an emailed link is for: a reset, or finishing an invited account. */
export interface PasswordLink {
  purpose: 'reset' | 'invite';
  email: string;
  fullName: string;
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ mfaRequired: boolean }>('/auth/login', { email, password }),
  submitMfa: (code: string) => api.post<{ ok: true }>('/auth/mfa', { code }),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
  me: () => api.get<MeResponse>('/auth/me'),
  forgotPassword: (email: string) => api.post<{ ok: true }>('/auth/password/forgot', { email }),
  describeLink: (token: string) => api.post<PasswordLink>('/auth/password/link', { token }),
  setPassword: (token: string, password: string) =>
    api.post<{ purpose: PasswordLink['purpose']; email: string }>('/auth/password/set', {
      token,
      password,
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: true }>('/auth/password/change', { currentPassword, newPassword }),
};

/** Mirrors MIN_PASSWORD_LENGTH in the API, which is what actually enforces it. */
export const MIN_PASSWORD_LENGTH = 12;
